/**
 * Power arithmetic for the load schedule. Pure: no Prisma, no React, no I/O,
 * so every number here is testable in isolation (`npm run test:power`).
 *
 * The chain, in this order and never short-cut:
 *
 *     published W  →  wall-side W  →  VA (power factor)  →  A (voltage)  →  capacity
 *
 * FOUR THINGS THIS MODULE DELIBERATELY WILL NOT DO. They are not omissions:
 *
 * 1. **It does not size breakers or cable.** It reports load against a
 *    protective device the user states. Conductor ampacity, grouping and
 *    derating are a licensed electrician's call under the local authority.
 * 2. **It does not compute inrush.** No steady-state wattage predicts it.
 *    We flag an inrush class and count PSUs per group; we never name a curve.
 * 3. **It does not compute neutral current.** The vector formula holds only
 *    for linear loads, and a broadcast rack is close to 100% non-linear —
 *    triplen harmonics add in the neutral rather than cancelling. Printing a
 *    lower bound the reader cannot recognise as a lower bound is worse than
 *    printing nothing.
 * 4. **It does not apply a continuous-load derate by default.** Indonesia
 *    follows PUIL 2011 (SNI 0225:2011), which is IEC-derived: the rule is
 *    Ib ≤ In ≤ Iz, with the 1.45 factor already inside IEC 60898-1's
 *    conventional currents. The NEC 80% rule is offered as a policy and must
 *    never print as "per code" on an Indonesian job.
 *
 * Unknown is a first-class answer. A row with no figure enters no total; it is
 * counted and named. Nothing here ever substitutes a guess for a measurement.
 */

/* ------------------------------------------------------------------ types */

export type PowerSource =
  | 'MAINS_AC'
  | 'DC_ADAPTER'
  | 'POE'
  | 'POC'
  | 'BUS_POWERED'
  | 'BATTERY'
  | 'UPSTREAM_PSU'
  | 'NONE'
  | 'UNKNOWN'

export type InrushClass = 'NONE' | 'PSU' | 'LAMP' | 'MOTOR' | 'TRANSFORMER'

export type PfPreset = 'RESISTIVE' | 'MOTOR' | 'SWITCHING'

/** Why a row contributes nothing to the mains total. */
export type ZeroReason =
  | 'INSIDE_HOST'
  | 'NONE_SOURCE'
  | 'BATTERY'
  | 'BUS_POWERED'
  | 'POC'
  | 'POE'
  | 'UPSTREAM_PSU'
  | 'UNKNOWN_DRAW'

export type PsuMode = 'SINGLE' | 'REDUNDANT' | 'ALTERNATIVE'

/* -------------------------------------------------------------- constants */

/**
 * Every constant is printed on the export. A number a reader cannot trace is
 * a number they cannot argue with, and this document exists to be argued with.
 */
export const POWER_CONSTANTS = {
  /** SPLN 1:1995 nominal line-to-neutral. */
  voltage: 230,
  frequency: 50,
  /** −10% of nominal: the worst-case-voltage toggle. */
  worstCaseVoltage: 207,
  /** PLN computes its own daya tiers at 220 V (7700 / 35 = 220 exactly). */
  plnTierVoltage: 220,
  /** Three-phase line voltage. */
  lineVoltage: 400,
  /** External DC brick: published draw is measured DC-side, so gross it up. */
  adapterEff: 0.85,
  /** 802.3at PSE/PD ratio 25.5/30 — cable loss between PSE port and PD. */
  poeCableEff: 0.85,
  /** AC watts per watt delivered at the PSE port (Cisco C9200-48P). */
  poePseOverhead: 1.148,
  /** Double-conversion UPS. Editable per row. */
  upstreamEff: 0.93,
  /** kVA→kW rating convention for a genset. */
  gensetPf: 0.8,
  /** IEC: no continuous-load derate. NEC policy sets this to 80. */
  usablePct: 100,
} as const

/** Cable-loss efficiency by PoE type: 1=af, 2=at, 3=bt Type 3, 4=bt Type 4. */
export const POE_CABLE_EFF: Record<number, number> = { 1: 0.84, 2: 0.85, 3: 0.85, 4: 0.79 }

/** Standard MCB ratings (A), IEC 60898-1. */
export const MCB_STEPS = [2, 4, 6, 10, 16, 20, 25, 32, 40, 50, 63] as const

/** IEC 60309 ("CEE") connector ratings in amps. */
export const CEE_RATINGS = [16, 32, 63, 125] as const

/**
 * PLN single-phase connected-power tiers (VA) and the MCB each is stamped
 * with. PLN's arithmetic uses 220 V, which is why 7700/220 = 35 exactly.
 */
export const PLN_DAYA_TIERS = [
  { va: 900, amps: 4 },
  { va: 1300, amps: 6 },
  { va: 2200, amps: 10 },
  { va: 3500, amps: 16 },
  { va: 4400, amps: 20 },
  { va: 5500, amps: 25 },
  { va: 7700, amps: 35 },
  { va: 11000, amps: 50 },
  { va: 13200, amps: 60 },
  { va: 16500, amps: 75 },
  { va: 23000, amps: 100 },
] as const

/**
 * Rough magnitude of an unknown box, by class, in watts.
 *
 * These are NEVER summed and never enter a total — a guessed watt in a load
 * schedule is worse than an admitted gap. Their only job is to say how badly a
 * missing figure could be hurting the answer, so "4 rows have no figure" can
 * become "one is a computer, this could be low by a third". A magnitude the
 * reader can weigh beats a count they will ignore.
 *
 * Deliberately pitched at the upper end of each class: the warning exists to
 * be wrong in the direction that gets checked.
 */
export const CATEGORY_TYPICAL_W: Record<string, number> = {
  COMPUTER: 900,
  MONITOR: 250,
  SWITCHER: 250,
  ROUTER: 200,
  RECORDER: 120,
  ENCODER: 120,
  MULTIVIEWER: 120,
  AUDIO: 100,
  CAMERA: 90,
  INTERCOM: 80,
  NETWORK: 80,
  POWER: 60,
  SYNC: 40,
  CONVERTER: 20,
  CAPTURE_CARD: 20,
  OTHER: 100,
}

/** POWER connectors that mean "plugged into mains". */
export const MAINS_CONNECTORS = new Set([
  'IEC C13',
  'IEC C14',
  'IEC C19',
  'IEC C20',
  'powerCON',
  'powerCON TRUE1',
  'Schuko',
  'NEMA 5-15',
])

/** POWER connectors fed from an external brick or a DC rail. */
export const DC_CONNECTORS = new Set([
  'XLR4 (DC)',
  'DC Barrel 12V',
  'DC Barrel 5V',
  'DC Barrel 24V',
  'DC 2-pin Locking',
  'Hirose 4-pin',
  '4-pin Molex',
  'USB-C (Power)',
  'Micro USB (Power)',
  'USB Mini-B (Power)',
])

/** POWER connectors that are a battery mount, never a wall load. */
export const BATTERY_CONNECTORS = new Set(['D-Tap', 'Anton Bauer'])

/* ------------------------------------------------------- per-row: wattage */

export type DrawFigures = {
  powerDrawTypicalW?: number | null
  powerDrawMaxW?: number | null
}

/**
 * Resolves the two draw figures. Where only one exists it is a MAXIMUM and
 * stands in for typical too, which is why the export prints how many rows had
 * no separate typical figure — the demand column is optimistic by exactly that
 * much and the reader deserves to know.
 */
export function resolveDraw(row: DrawFigures): { typicalW: number | null; maxW: number | null } {
  const maxW = row.powerDrawMaxW ?? null
  const typicalW = row.powerDrawTypicalW ?? maxW
  return { typicalW, maxW }
}

/**
 * Published watts → watts drawn at the wall. A DC brick's published figure is
 * measured at the DC output, so the adapter's own loss is added.
 */
export function wallWatts(watts: number, source: PowerSource): number {
  return source === 'DC_ADAPTER' ? watts / POWER_CONSTANTS.adapterEff : watts
}

/* -------------------------------------------------- per-row: power factor */

export type PfInput = {
  /** A figure stated on the row wins over everything. */
  powerFactor?: number | null
  pfPreset?: PfPreset | null
  powerSource: PowerSource
  wallW: number | null
}

/**
 * A total function — every branch returns. The earlier category-keyed rule
 * left a 200 W monitor, an LED processor and an amplifier undefined, and the
 * gap between the plausible answers there is 46%.
 *
 * The 75 W threshold is not a convention: IEC/EN 61000-3-2 forces active PFC
 * on equipment above it, which is a fact about the supply regardless of what
 * the box does.
 */
export function classPf(input: PfInput): number {
  if (input.powerFactor != null) return input.powerFactor
  if (input.pfPreset === 'RESISTIVE') return 1.0
  if (input.pfPreset === 'MOTOR') return 0.8
  if (input.pfPreset === 'SWITCHING') return 0.95
  if (input.powerSource === 'DC_ADAPTER') return 0.65
  if (input.wallW == null) return 0.9
  if (input.wallW >= 75) return 0.95
  return 0.65
}

/* ---------------------------------------------------------- VA and amps */

export const wattsToVa = (wallW: number, pf: number): number => wallW / pf

export const vaToAmps = (va: number, voltage: number = POWER_CONSTANTS.voltage): number => va / voltage

/**
 * Balanced three-phase line current. Note this is NOT total/3 amps — the √3
 * is why. Used only for the whole-plan figure; per-leg current is computed
 * from that leg's own VA at the line-to-neutral voltage.
 */
export const vaToAmps3Phase = (va: number, lineVoltage: number = POWER_CONSTANTS.lineVoltage): number =>
  va / (Math.sqrt(3) * lineVoltage)

/* ------------------------------------------------------------- redundancy */

export type PowerInlet = { connector: string; count?: number | null }

/**
 * Reads a device's power inlets and decides whether it is genuinely 2N.
 *
 * The test is the maximum count *within one connector group*, not the number
 * of distinct connectors. Two devices in the shipped catalogue break the naive
 * rule: an ATEM Television Studio HD8 Pro has two IEC C14 inlets *plus* a DC
 * barrel, and the AVMatrix SC2030 has two DC barrels *plus* a Micro USB. Both
 * are redundant with two cords; counting distinct connectors calls them
 * "alternative" and halves the outlets they need.
 */
export function inferPsuMode(inlets: PowerInlet[]): { mode: PsuMode; psuCount: number } {
  const byConnector = new Map<string, number>()
  let total = 0
  for (const inlet of inlets) {
    const n = Math.max(1, inlet.count ?? 1)
    byConnector.set(inlet.connector, (byConnector.get(inlet.connector) ?? 0) + n)
    total += n
  }
  const maxInGroup = Math.max(0, ...byConnector.values())
  if (maxInGroup >= 2) return { mode: 'REDUNDANT', psuCount: maxInGroup }
  if (total >= 2) return { mode: 'ALTERNATIVE', psuCount: 1 }
  return { mode: 'SINGLE', psuCount: 1 }
}

/** Best guess at how a device takes power, from its inlets alone. */
export function inferPowerSource(inlets: PowerInlet[]): PowerSource {
  if (inlets.length === 0) return 'UNKNOWN'
  // Mains wins even when a DC or battery option exists: spec sheets are
  // explicit that a DC IN takes priority over PoE, and a mains inlet is the
  // one a planner has to find an outlet for.
  if (inlets.some((i) => MAINS_CONNECTORS.has(i.connector))) return 'MAINS_AC'
  if (inlets.some((i) => DC_CONNECTORS.has(i.connector))) return 'DC_ADAPTER'
  if (inlets.every((i) => BATTERY_CONNECTORS.has(i.connector))) return 'BATTERY'
  return 'UNKNOWN'
}

/* -------------------------------------------------------------- PoE chain */

/**
 * Watts a PoE switch must pull from the wall to deliver `pdWatts` to its
 * powered devices, as two named steps. One combined fudge factor
 * under-attributes the switch's own feed by 10–15%.
 */
export function poeChain(pdWatts: number, poeType: number | null | undefined) {
  const cableEff = POE_CABLE_EFF[poeType ?? 2] ?? POE_CABLE_EFF[2] ?? 0.85
  const pseW = pdWatts / cableEff
  return { pseW, hostAcW: pseW * POWER_CONSTANTS.poePseOverhead }
}

/* ------------------------------------------------------------ imbalance */

/**
 * NEMA MG 1 percentage unbalance: max deviation from the mean, over the mean.
 *
 * Deliberately not (max−min)/max, which reads systematically high — 33% where
 * NEMA says 19.6% on the same rig. A threshold that cries wolf is ignored on
 * the job where it matters.
 */
export function nemaUnbalance(legs: number[]): number {
  const live = legs.filter((v) => v > 0)
  if (live.length === 0) return 0
  const mean = legs.reduce((a, b) => a + b, 0) / legs.length
  if (mean === 0) return 0
  const maxDeviation = Math.max(...legs.map((v) => Math.abs(v - mean)))
  return (maxDeviation / mean) * 100
}

/** The practitioner's rule of thumb, printed alongside NEMA so neither misleads. */
export function spread(legs: number[]): number {
  const max = Math.max(...legs)
  if (max === 0) return 0
  return ((max - Math.min(...legs)) / max) * 100
}

/* --------------------------------------------------------- supply checks */

export type GensetVerdict = {
  kvaUtil: number
  kwUtil: number
  /** The binding constraint — the higher of the two. */
  util: number
  binds: 'kVA' | 'kW'
  verdict: 'OVER' | 'OK' | 'OVERSIZED'
  note: string | null
}

/**
 * Checks a genset on both axes, because kVA and kW bind differently: the
 * alternator is limited by current (kVA) and the engine by real power (kW),
 * and a set rated in kVA assumes pf 0.8.
 *
 * `requiredVA` and `requiredW` must ALREADY carry the headroom. Applying it
 * again here is the classic bug — it pushes you a whole frame size up.
 */
export function gensetCheck(requiredVA: number, requiredW: number, gensetKVA: number): GensetVerdict {
  const kvaUtil = (requiredVA / 1000 / gensetKVA) * 100
  const kwUtil = (requiredW / 1000 / (gensetKVA * POWER_CONSTANTS.gensetPf)) * 100
  const util = Math.max(kvaUtil, kwUtil)
  const binds = kvaUtil >= kwUtil ? 'kVA' : 'kW'
  if (util > 100) {
    return {
      kvaUtil, kwUtil, util, binds, verdict: 'OVER',
      note: `Over capacity on ${binds}. Take the next set up.`,
    }
  }
  if (util < 30) {
    return {
      kvaUtil, kwUtil, util, binds, verdict: 'OVERSIZED',
      note: 'Oversized — sustained light load causes wet stacking and cylinder glazing. Ask about a smaller set or a load bank.',
    }
  }
  return { kvaUtil, kwUtil, util, binds, verdict: 'OK', note: null }
}

/** The smallest PLN tier that carries `va`, and the tier below it if it does not fit. */
export function plnTier(va: number) {
  const amps = va / POWER_CONSTANTS.plnTierVoltage
  const fits = PLN_DAYA_TIERS.find((t) => t.va >= va) ?? null
  return { requiredAmpsAtPlnVoltage: amps, tier: fits }
}

/* ------------------------------------------------- what to actually ask for */

/**
 * Generator sizes people actually hire, in kVA. Not a continuous scale — you
 * ring a supplier and they have these.
 */
export const GENSET_LADDER = [10, 15, 20, 25, 30, 40, 60, 80, 100, 125, 150, 200, 250] as const

/**
 * The smallest set a hire company will realistically send to an event in
 * Indonesia. Below this the answer is always "you are fine", which is worth
 * saying plainly instead of making someone configure a check that cannot fail.
 */
export const SMALLEST_HIRE_KVA = 40

/** Buffer added to the estimate before rounding. Never quote a figure with none. */
export const RECOMMENDATION_BUFFER_PCT = 25

export type SupplyRecommendation = {
  /** What the gear draws, everything on at once. */
  estimateVa: number
  estimateW: number
  /** The estimate plus the buffer — what the recommendation is sized against. */
  bufferedVa: number
  /** Next real set off the hire ladder, or null if past the largest. */
  gensetKva: number | null
  /** True when any set a hire company would send already covers it. */
  anyHireSetCovers: boolean
  /** A number to say out loud to a venue engineer, in kVA. */
  buildingAskKva: number
}

/**
 * Turns a total into the two sentences a technical director actually needs:
 * which generator to hire, and what to ask a venue for.
 *
 * Sized on the peak — everything on at once — because the failure that costs a
 * show is under-ordering, and the cost of over-ordering is a slightly bigger
 * set for the same day rate.
 *
 * The generator is chosen against BOTH its kVA and its kW: a 20 kVA set is only
 * 16 kW at the 0.8 rating convention, so a modern PFC rack can fit the
 * alternator and still stall the engine.
 */
export function recommendSupply(estimateVa: number, estimateW: number): SupplyRecommendation {
  const bufferedVa = estimateVa * (1 + RECOMMENDATION_BUFFER_PCT / 100)
  const bufferedW = estimateW * (1 + RECOMMENDATION_BUFFER_PCT / 100)

  const gensetKva =
    GENSET_LADDER.find((kva) => kva * 1000 >= bufferedVa && kva * 1000 * gensetPfConvention >= bufferedW) ?? null

  return {
    estimateVa,
    estimateW,
    bufferedVa,
    gensetKva,
    anyHireSetCovers: gensetKva != null && gensetKva <= SMALLEST_HIRE_KVA,
    buildingAskKva: roundAskKva(bufferedVa / 1000),
  }
}

const gensetPfConvention = POWER_CONSTANTS.gensetPf

/**
 * A figure someone will say into a phone. Under 20 kVA rounds to the next 5,
 * above it to the next 10 — nobody asks a hotel engineer for 23 kVA.
 */
export function roundAskKva(kva: number): number {
  if (kva <= 0) return 0
  // A small show asking a hotel for 5 kVA when it needs 0.4 is how you get
  // charged for a temporary supply you never use. Steps grow with the number.
  const step = kva < 5 ? 1 : kva < 20 ? 5 : 10
  return Math.ceil(kva / step) * step
}

/** Smallest standard MCB at or above `amps`, or null if past 63 A. */
export const nextMcb = (amps: number): number | null => MCB_STEPS.find((a) => a >= amps) ?? null

/* ---------------------------------------------------------------- presets */

export type Preset = {
  key: string
  label: string
  /** Watts at the connected (max) basis. */
  connectedW: number
  /** Watts at the demand (typical) basis, or a function of plan settings. */
  demandW: number | ((settings: { contentFraction: number }) => number)
  pf: number
  inrush: InrushClass
  source: PowerSource
  /** Charged per unit of this, e.g. m² for an LED wall. */
  unit?: string
  note?: string
}

/**
 * The loads ROUTR will never catalogue, and where most of a show's watts
 * actually live. Without these the tool totals a video rack and calls it an
 * event.
 */
export const PRESETS: Preset[] = [
  {
    key: 'led-wall',
    label: 'LED wall panel',
    unit: 'm²',
    connectedW: 800,
    demandW: ({ contentFraction }) => 800 * contentFraction,
    pf: 0.95,
    inrush: 'PSU',
    source: 'MAINS_AC',
    note: 'Peak is full-white. Average content draws far less — the content slider is the single biggest lever on the total.',
  },
  { key: 'led-processor', label: 'LED processor', connectedW: 150, demandW: 150, pf: 0.95, inrush: 'PSU', source: 'MAINS_AC' },
  { key: 'moving-head', label: 'Moving head fixture', connectedW: 300, demandW: 300, pf: 0.95, inrush: 'MOTOR', source: 'MAINS_AC' },
  { key: 'fresnel-1k', label: 'Fresnel 1 kW tungsten', connectedW: 1000, demandW: 1000, pf: 1.0, inrush: 'LAMP', source: 'MAINS_AC' },
  { key: 'followspot', label: 'Followspot 1.2 kW', connectedW: 1200, demandW: 1200, pf: 1.0, inrush: 'LAMP', source: 'MAINS_AC' },
  { key: 'monitor-24', label: 'Production monitor 24"', connectedW: 45, demandW: 45, pf: 0.95, inrush: 'PSU', source: 'MAINS_AC' },
  { key: 'workstation', label: 'Replay / vMix workstation', connectedW: 650, demandW: 450, pf: 0.95, inrush: 'PSU', source: 'MAINS_AC' },
  { key: 'comms-base', label: 'Comms base station', connectedW: 60, demandW: 60, pf: 0.95, inrush: 'PSU', source: 'MAINS_AC' },
  { key: 'charger-bay', label: 'V-mount charger bay', unit: 'bay', connectedW: 90, demandW: 90, pf: 0.95, inrush: 'PSU', source: 'MAINS_AC' },
  { key: 'ups-1500', label: 'Rack UPS 1500 VA', connectedW: 35, demandW: 35, pf: 0.95, inrush: 'TRANSFORMER', source: 'MAINS_AC', note: 'Idle draw only; downstream load is attributed through it at η 0.93.' },
  { key: 'split-ac', label: 'Split AC 1 PK', connectedW: 900, demandW: 900, pf: 0.8, inrush: 'MOTOR', source: 'MAINS_AC' },
  { key: 'urn', label: 'Catering urn', connectedW: 2000, demandW: 2000, pf: 1.0, inrush: 'NONE', source: 'MAINS_AC' },
]

export const presetByKey = (key: string): Preset | undefined => PRESETS.find((p) => p.key === key)

/** Resolves a preset's demand figure against the plan's settings. */
export function presetDemandW(preset: Preset, settings: { contentFraction: number }): number {
  return typeof preset.demandW === 'function' ? preset.demandW(settings) : preset.demandW
}

/* -------------------------------------------------------- class defaults */

/**
 * Median draw per category, regenerated from the catalogue by
 * `scripts/power-class-stats.ts`. OFF by default and never counted as known:
 * an estimate that quietly joins a total is how a plan becomes wrong without
 * anyone noticing.
 */
export const POWER_CLASS_DEFAULTS: Record<string, number> = {
  CONVERTER: 6,
  CAPTURE_CARD: 24,
  ENCODER: 30,
  SWITCHER: 28.5,
  CAMERA: 21,
  ROUTER: 60,
  MULTIVIEWER: 25,
  RECORDER: 30,
  MONITOR: 45,
  AUDIO: 40,
  INTERCOM: 60,
  SYNC: 15,
  NETWORK: 50,
  COMPUTER: 450,
  POWER: 10,
  OTHER: 20,
}

/* ------------------------------------------------------------- formatting */

export const round = (n: number, dp = 2): number => Math.round(n * 10 ** dp) / 10 ** dp

/** "1.24 kVA" / "624 VA" — VA is never shown to more precision than it is known. */
export function formatVa(va: number): string {
  return va >= 1000 ? `${round(va / 1000, 2)} kVA` : `${Math.round(va)} VA`
}

export function formatW(w: number): string {
  return w >= 1000 ? `${round(w / 1000, 2)} kW` : `${Math.round(w)} W`
}

export const formatAmps = (a: number): string => `${round(a, 2)} A`
